/**
 * Canonical Service
 * Generates canonical links for SEO
 */

export class CanonicalService {
	constructor() {
		this.baseUrl = this.getBaseUrl();
	}

	/**
	 * Get the base URL for the application
	 */
	getBaseUrl() {
		if (typeof window !== "undefined") {
			return `${window.location.protocol}//${window.location.host}`;
		}
		return "https://gps.solenis.com";
	}

	/**
	 * Generate canonical URL for the current route
	 * Always points to the English production version
	 * @param {Object} route - Vue Router route object
	 * @returns {string} Canonical URL
	 */
	generateCanonicalUrl(route) {
		// Get the current path without language prefix
		const currentPath = this.getPathWithoutLanguage(route.path);

		// Always use English (/en) for canonical URLs
		const canonicalUrl = `https://gps.solenis.com/en${currentPath}`;

		return canonicalUrl;
	}

	/**
	 * Remove language prefix from path to get the base path
	 * @param {string} path - Current route path
	 * @returns {string} Path without language prefix
	 */
	getPathWithoutLanguage(path) {
		// Remove leading slash and language code
		const pathParts = path.split("/").filter((part) => part);

		// Available language codes
		const availableLanguages = [
			"en",
			"es",
			"fr",
			"de",
			"it",
			"pt",
			"nl",
			"sv",
			"da",
			"no",
			"fi",
		];

		// Check if first part is a language code
		if (pathParts.length > 0 && availableLanguages.includes(pathParts[0])) {
			// Remove the language part
			pathParts.shift();
		}

		// Reconstruct path
		return pathParts.length > 0 ? `/${pathParts.join("/")}` : "";
	}

	/**
	 * Inject canonical link into the document head
	 * @param {string} canonicalUrl - Canonical URL
	 */
	injectCanonicalLink(canonicalUrl) {
		// Remove existing canonical link
		this.removeExistingCanonicalLink();

		// Add new canonical link
		const link = document.createElement("link");
		link.rel = "canonical";
		link.href = canonicalUrl;
		document.head.appendChild(link);
	}

	/**
	 * Remove existing canonical link from document head
	 */
	removeExistingCanonicalLink() {
		const existingLink = document.querySelector("link[rel='canonical']");
		if (existingLink) {
			existingLink.remove();
		}
	}

	/**
	 * Generate canonical URL for a specific route and inject it
	 * @param {Object} route - Vue Router route object
	 */
	generateAndInjectCanonicalLink(route) {
		const canonicalUrl = this.generateCanonicalUrl(route);
		this.injectCanonicalLink(canonicalUrl);
		return canonicalUrl;
	}
}

// Export singleton instance
export const canonicalService = new CanonicalService();
